import { useState, useEffect, useCallback } from "react";
import { apiFetch, listTenants, updateTenant } from "../lib/api";
import type { Tenant } from "../types";

interface RecorderTokenRow {
  id: string;
  tenant_id: string;
  label: string;
  status: string;
  created_at: number;
  last_used_at: number | null;
  expires_at: number | null;
  setup_expires_at: number;
  bot_name?: string | null;
}

interface OutboxCount {
  status: string;
  n: number;
  last_updated: number | null;
}

interface OutboxRow {
  event_id: string;
  tenant_id: string;
  status: string;
  attempts: number;
  last_error?: string | null;
  last_http_status?: number | null;
  updated_at: number;
}

function fmtTime(ms: number | null | undefined): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString("zh-HK", { hour12: false });
}

export default function VoiceAssistantPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tokens, setTokens] = useState<RecorderTokenRow[]>([]);
  const [outboxCounts, setOutboxCounts] = useState<OutboxCount[]>([]);
  const [outboxRecent, setOutboxRecent] = useState<OutboxRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [issueTenant, setIssueTenant] = useState("");
  const [issueLabel, setIssueLabel] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [issuedUrl, setIssuedUrl] = useState("");
  const [issueMsg, setIssueMsg] = useState("");
  const [toggling, setToggling] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const opcollabFor = (tier: string | undefined) => /^(pro|legacy)/i.test(tier || "");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [tRes, kRes, oRes] = await Promise.all([
        listTenants(),
        apiFetch("/admin/voice-assistant/tokens"),
        apiFetch("/admin/voice-assistant/outbox"),
      ]);
      if (tRes.success) setTenants(tRes.tenants || []);
      const kData = await kRes.json();
      setTokens(kData.tokens || []);
      const oData = await oRes.json();
      setOutboxCounts(oData.counts || []);
      setOutboxRecent(oData.recent || []);
    } catch {
      setError("載入失敗，請重新整理");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function issueToken() {
    if (!issueTenant.trim()) {
      setIssueMsg("請先選擇租戶");
      return;
    }
    setIssuing(true);
    setIssuedUrl("");
    setIssueMsg("");
    try {
      const res = await apiFetch("/admin/voice-assistant/tokens", {
        method: "POST",
        body: JSON.stringify({ tenant_id: issueTenant.trim(), label: issueLabel.trim() }),
      });
      const data = await res.json();
      if (data.ok) {
        setIssuedUrl(data.setup_url);
        setIssueMsg(`✅ 已簽發（15 分鐘內有效）——複製後發給商家打開`);
        setIssueLabel("");
        void load();
      } else {
        setIssueMsg(`❌ ${data.error || "簽發失敗"}`);
      }
    } catch {
      setIssueMsg("❌ 網絡錯誤");
    } finally {
      setIssuing(false);
    }
  }

  async function revokeToken(id: string) {
    if (!window.confirm(`撤銷通行證 ${id}？撤銷後該裝置立即失效。`)) return;
    const res = await apiFetch(`/admin/voice-assistant/tokens/${id}/revoke`, { method: "POST" });
    const data = await res.json();
    if (!data.ok) window.alert(data.error || "撤銷失敗");
    void load();
  }

  async function toggleSecretary(tenant: Tenant) {
    setToggling(tenant.tenant_id);
    try {
      await updateTenant(tenant.tenant_id, { secretary_enabled: tenant.secretary_enabled === 1 ? 0 : 1 });
      void load();
    } finally {
      setToggling(null);
    }
  }

  async function replayDead(eventId: string) {
    if (!window.confirm(`重放事件 ${eventId}？`)) return;
    // recorder 路由（同 ai-core，admin key 認證）
    const res = await apiFetch(`/api/vn/recorder/outbox/${encodeURIComponent(eventId)}/replay`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (data.ok) {
      window.alert("已重排投遞");
      void load();
    } else {
      window.alert(data.error || "重放失敗（事件可能非 dead 狀態）");
    }
  }

  function copyIssued() {
    if (!issuedUrl) return;
    void navigator.clipboard.writeText(issuedUrl).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  const deadRows = outboxRecent.filter((r) => r.status === "dead");

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">🎙️ 語音服務運維台</h1>
          <p className="text-sm opacity-70">錄音台通行證 · 租戶開關 · OPCollab 情報同步（Pro 內含增值服務）</p>
        </div>
        <button className="px-3 py-1.5 rounded bg-[var(--accent,#4f7cff)] text-white text-sm" onClick={() => void load()}>
          🔄 重新載入
        </button>
      </header>

      {error && <div className="p-3 rounded bg-red-500/15 text-red-400 text-sm">{error}</div>}
      {loading && <div className="text-sm opacity-60">載入中…</div>}

      {/* 卡一：Recorder 通行證 */}
      <section className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
        <h2 className="font-semibold">🎫 錄音台通行證（一次性登入連結）</h2>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            className="px-2 py-1.5 rounded bg-black/30 border border-white/15 text-sm"
            value={issueTenant}
            onChange={(e) => setIssueTenant(e.target.value)}
          >
            <option value="">— 選擇租戶 —</option>
            {tenants.map((t) => (
              <option key={t.tenant_id} value={t.tenant_id}>
                {t.tenant_id}{t.bot_name ? `（${t.bot_name}）` : ""}
              </option>
            ))}
          </select>
          <input
            className="px-2 py-1.5 rounded bg-black/30 border border-white/15 text-sm"
            placeholder="備註（給誰用，可留空）"
            value={issueLabel}
            onChange={(e) => setIssueLabel(e.target.value)}
          />
          <button
            className="px-3 py-1.5 rounded bg-[var(--accent,#4f7cff)] text-white text-sm disabled:opacity-50"
            disabled={issuing}
            onClick={() => void issueToken()}
          >
            {issuing ? "簽發中…" : "簽發一次性連結"}
          </button>
        </div>
        {issuedUrl && (
          <div className="p-2 rounded bg-black/30 border border-white/10 text-xs break-all">
            <div className="flex items-center gap-2">
              <code>{issuedUrl}</code>
              <button className="px-2 py-0.5 rounded border border-white/20" onClick={copyIssued}>
                {copied ? "✅ 已複製" : "複製"}
              </button>
            </div>
          </div>
        )}
        {issueMsg && <p className="text-xs opacity-75">{issueMsg}</p>}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="opacity-60 text-left">
              <tr>
                <th className="p-1.5">Token</th>
                <th className="p-1.5">租戶</th>
                <th className="p-1.5">備註</th>
                <th className="p-1.5">狀態</th>
                <th className="p-1.5">最後使用</th>
                <th className="p-1.5">到期</th>
                <th className="p-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((k) => (
                <tr key={k.id} className="border-t border-white/5">
                  <td className="p-1.5 font-mono">{k.id.slice(0, 11)}…</td>
                  <td className="p-1.5">{k.tenant_id}</td>
                  <td className="p-1.5">{k.label || "—"}</td>
                  <td className="p-1.5">
                    {k.status === "active" ? "🟢 使用中" : k.status === "pending" ? "⏳ 待開通" : "⛔ 已撤銷"}
                  </td>
                  <td className="p-1.5">{fmtTime(k.last_used_at)}</td>
                  <td className="p-1.5">{fmtTime(k.expires_at)}</td>
                  <td className="p-1.5">
                    {k.status !== "revoked" && (
                      <button className="px-2 py-0.5 rounded border border-red-400/40 text-red-300" onClick={() => void revokeToken(k.id)}>
                        撤銷
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!tokens.length && (
                <tr>
                  <td colSpan={7} className="p-3 text-center opacity-50">
                    尚未簽發任何通行證
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* 卡二：租戶開關 */}
      <section className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
        <h2 className="font-semibold">🔌 租戶開關（語音功能 · OPCollab 增值）</h2>
        <p className="text-xs opacity-60">OPCollab 情報同步為 Pro 套餐內含（legacy 戰略夥伴同級）；開關隨方案自動生效，無需人手設定。</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="opacity-60 text-left">
              <tr>
                <th className="p-1.5">租戶</th>
                <th className="p-1.5">名稱</th>
                <th className="p-1.5">方案</th>
                <th className="p-1.5">語音功能</th>
                <th className="p-1.5">OPCollab 增值</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.tenant_id} className="border-t border-white/5">
                  <td className="p-1.5 font-mono">{t.tenant_id}</td>
                  <td className="p-1.5">{t.bot_name || "—"}</td>
                  <td className="p-1.5">{t.plan_tier || "—"}</td>
                  <td className="p-1.5">
                    <button
                      className={`px-2 py-0.5 rounded border text-xs ${
                        t.secretary_enabled === 1 ? "border-emerald-400/40 text-emerald-300" : "border-white/20 opacity-70"
                      }`}
                      disabled={toggling === t.tenant_id}
                      onClick={() => void toggleSecretary(t)}
                    >
                      {t.secretary_enabled === 1 ? "✅ 已啟用" : "◻ 未啟用"}
                    </button>
                  </td>
                  <td className="p-1.5">{opcollabFor(t.plan_tier) ? "💠 Pro 內含" : "🔒 需升級 Pro"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 卡三：OPCollab 連接健康 */}
      <section className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
        <h2 className="font-semibold">📡 OPCollab 情報同步健康</h2>
        <div className="flex flex-wrap gap-2 text-xs">
          {(["pending", "delivered", "dead"] as const).map((st) => {
            const row = outboxCounts.find((c) => c.status === st);
            const color = st === "delivered" ? "text-emerald-300" : st === "dead" ? "text-red-300" : "text-amber-300";
            return (
              <span key={st} className={`px-3 py-1.5 rounded bg-black/30 border border-white/10 ${color}`}>
                {st === "pending" ? "⏳ 待投遞" : st === "delivered" ? "✅ 已投遞" : "☠️ 死信"}：{row?.n ?? 0}
                {row?.last_updated ? `（更新 ${fmtTime(row.last_updated)}）` : ""}
              </span>
            );
          })}
          {!outboxCounts.length && <span className="opacity-50 px-3 py-1.5">尚無投遞事件（同步未啟用或未有錄音完成）</span>}
        </div>
        {!!deadRows.length && (
          <div className="space-y-1">
            <p className="text-xs font-semibold text-red-300">死信事件（可重放）</p>
            {deadRows.map((d) => (
              <div key={d.event_id} className="flex items-center gap-2 text-xs">
                <code className="opacity-70 truncate max-w-[50%]">{d.event_id}</code>
                <span className="opacity-60">HTTP {d.last_http_status ?? "—"} · 嘗試 {d.attempts} 次</span>
                <button className="px-2 py-0.5 rounded border border-amber-400/40 text-amber-300" onClick={() => void replayDead(d.event_id)}>
                  重放
                </button>
              </div>
            ))}
          </div>
        )}
        <details className="text-xs">
          <summary className="cursor-pointer opacity-70">最近 20 筆投遞記錄</summary>
          <table className="w-full mt-2">
            <thead className="opacity-60 text-left">
              <tr>
                <th className="p-1">事件</th>
                <th className="p-1">租戶</th>
                <th className="p-1">狀態</th>
                <th className="p-1">HTTP</th>
                <th className="p-1">更新</th>
              </tr>
            </thead>
            <tbody>
              {outboxRecent.map((r) => (
                <tr key={r.event_id} className="border-t border-white/5">
                  <td className="p-1 font-mono truncate max-w-[220px]">{r.event_id}</td>
                  <td className="p-1">{r.tenant_id}</td>
                  <td className="p-1">{r.status}</td>
                  <td className="p-1">{r.last_http_status ?? "—"}</td>
                  <td className="p-1">{fmtTime(r.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </section>
    </div>
  );
}
